/**
 * Honest community size (design `designs/01-now/2026-09-10-honest-community-size-design.md`).
 *
 * The four `GroupDTO` fields the server adds, typed by hand because `openapi.json` is deliberately not
 * regenerated in this change (AGENTS.md rule 6's precedent; dispatch 2026-09-10 decision D-6). Every
 * value is DECIDED on the server (`GroupActivityPolicy`): the floor, "New group", staleness and the
 * private-group rule. Read them only through `readGroupActivity`, never field by field, so the one
 * client rule ("activity, else New group, else nothing") lives in one place and nothing here can
 * re-derive the floor.
 */
export type GroupActivityFields = {
    activityShown?: boolean | null;
    visitorsThisWeek?: number | null;
    contributionsThisWeek?: number | null;
    isNewGroup?: boolean | null;
    /**
     * Round 38 D15 + D-C1 (cast parity): the group page header's member count, which is the ROSTER count
     * (`members_count`, cast included), decided on the server (`GroupActivityPolicy.membersShown`: hidden
     * for a private group's outsider and for zero). Replaces SV-139's real-member count. Only the group
     * page carries it, from `group.jte`'s `data-members-shown`; no API payload does. Read it through
     * `readMemberTotal`.
     */
    memberTotal?: number | null;
};

export type GroupActivity =
    | { kind: "activity"; visitorsThisWeek: number | null; contributionsThisWeek: number | null }
    | { kind: "new" }
    | { kind: "none" };

const NO_ACTIVITY: GroupActivity = { kind: "none" };

function positiveCount(value: unknown): number | null {
    const count = typeof value === "number" ? value : typeof value === "string" && value.trim() !== "" ? Number(value) : NaN;
    return Number.isFinite(count) && count > 0 ? Math.floor(count) : null;
}

/**
 * Any object that may carry the four fields: a generated `GroupDTO` (which does not declare them yet),
 * a hand-written row type, or `null`. Structural on purpose, so no caller has to cast.
 */
export function readGroupActivity(group: object | null | undefined): GroupActivity {
    if (!group) {
        return NO_ACTIVITY;
    }
    const fields = group as GroupActivityFields;
    if (fields.activityShown === true) {
        const visitorsThisWeek = positiveCount(fields.visitorsThisWeek);
        const contributionsThisWeek = positiveCount(fields.contributionsThisWeek);
        // The server never sends activityShown with both halves empty; if it ever did, show nothing
        // rather than an empty line.
        return visitorsThisWeek == null && contributionsThisWeek == null
            ? NO_ACTIVITY
            : { kind: "activity", visitorsThisWeek, contributionsThisWeek };
    }
    return fields.isNewGroup === true ? { kind: "new" } : NO_ACTIVITY;
}

/**
 * The same four values, plus the header-only member count (`data-members-shown`, round 38 D-C1), from the
 * group page's server-rendered `data-*` attributes (`group.jte`), which
 * arrive as strings. `normalizeProps` collapses `""` to `undefined`, which here correctly reads as
 * "nothing to show".
 */
export function readGroupActivityFromDataset(dataset: Record<string, string | undefined>): GroupActivityFields {
    return {
        activityShown: dataset.activityShown === "true",
        visitorsThisWeek: positiveCount(dataset.visitorsThisWeek),
        contributionsThisWeek: positiveCount(dataset.contributionsThisWeek),
        isNewGroup: dataset.isNewGroup === "true",
        memberTotal: positiveCount(dataset.membersShown),
    };
}

/** The header's server-decided member count (the roster count, cast included), or `null` for no chip. */
export function readMemberTotal(activity: GroupActivityFields | null | undefined): number | null {
    return positiveCount(activity?.memberTotal);
}
